import React, {
  useCallback,
  useEffect,
  useRef,
  SyntheticEvent,
  useState,
} from 'react';

import { Box } from '../../../../../../Box';
import { Typography } from '../../../../../../Typography';
import type { ReactPlayerProps } from '../types';
import { PlayerEntry } from '../helpers/PlayersEntries';
import { VideoFailed } from '../VideoFailed';

type PlayerType = React.ForwardRefExoticComponent<
  ReactPlayerProps & {
    activePlayer: PlayerEntry['player'];
  }
>;

export const Player: PlayerType = React.forwardRef((props, ref) => {
  const {
    playing,
    pip,
    playbackRate,
    volume,
    activePlayer: PlayerComponent,
    onReady,
    onStart,
    onLoadStart,
    onPlay,
  } = props;

  const [error, setError] = useState(false);

  const playerRef = useRef<HTMLVideoElement | null>(null);
  const startOnPlayRef = useRef(true);

  useEffect(() => {
    if (!playerRef.current) return;

    if (playerRef.current.paused && playing === true) {
      playerRef.current.play();
    }
    if (!playerRef.current.paused && playing === false) {
      playerRef.current.pause();
    }

    playerRef.current.playbackRate = playbackRate ?? 1;
    playerRef.current.volume = volume ?? 1;
  }, [playing, playbackRate, volume]);

  useEffect(() => {
    if (!playerRef.current || !globalThis.document) return;

    if (pip && !document.pictureInPictureElement) {
      try {
        playerRef.current.requestPictureInPicture?.();
      } catch (err) {}
    }

    if (!pip && document.pictureInPictureElement) {
      try {
        document.exitPictureInPicture?.();
      } catch (err) {}
    }
  }, [pip]);

  const handleLoadStart = (event: SyntheticEvent<HTMLVideoElement>) => {
    startOnPlayRef.current = true;

    onReady?.();
    onLoadStart?.(event);
  };

  const handlePlay = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (startOnPlayRef.current) {
      startOnPlayRef.current = false;

      onStart?.(event);
    }

    onPlay?.(event);
  };

  const handleVideoError = () => {
    setError(() => true);
  };

  const getPlayerRef = useCallback(
    (node: HTMLVideoElement) => {
      playerRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }
    },
    [ref],
  );

  if (!PlayerComponent) {
    return null;
  }

  if (error) {
    return <VideoFailed width={props.width} height={props.height} />;
  }

  return (
    <Box display="flex" flexDirection="column" gap="12">
      <PlayerComponent
        {...props}
        style={props.style}
        className={props.className}
        slot={props.slot}
        ref={getPlayerRef}
        src={props.src}
        crossOrigin={props.crossOrigin}
        preload={props.preload}
        controls={props.controls}
        muted={props.muted}
        autoPlay={props.autoPlay}
        loop={props.loop}
        playsInline={props.playsInline}
        config={props.config}
        onLoadStart={handleLoadStart}
        onPlay={handlePlay}
        onError={handleVideoError}
      >
        {props.children}
      </PlayerComponent>

      {props.description && (
        <Typography variant="caption" color="text.light.tertirary">
          {props.description}
        </Typography>
      )}
    </Box>
  );
});
